/** @jsxImportSource @emotion/react */
import { FC, memo } from 'react';
import { useTranslation } from 'react-i18next';
import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import {
  Badge,
  Button,
  css,
  Grid,
  Grow,
  Typography,
  useTheme,
} from '@mui/material';
import DishImageTile from 'components/DishImageTile/DishImageTile';
import ItemCard from 'components/ItemCard';
import { MAIN_BUTTON_HEIGHT } from 'config/constants';
import { useScrollIntoViewIfNeeded } from 'hooks/useScrollIntoViewIfNeeded';
import MenuItemNotAvailable from 'pages/Menu/components/MenuItemNotAvailable';
import { TXT_SELECT } from 'translations';
import {
  menuItemImages,
  menuItemName,
  menuItemPortionLabel,
} from 'utils/menuItem';
import { formatCurrency } from 'utils/string/formatCurrency';
import { Props } from './types';

const MenuItemCard: FC<Props> = ({
  item,
  quantity = 0,
  onAdd,
  onRemove,
  inCart,
  editable,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { elementRef, scrollIntoViewIfNeeded } =
    useScrollIntoViewIfNeeded<HTMLDivElement>({
      align: 'bottom',
      offset: MAIN_BUTTON_HEIGHT + parseFloat(theme.spacing(2)),
    });

  const itemName = menuItemName(item);
  const title = [itemName, menuItemPortionLabel(item, t)]
    .filter((part) => !!part)
    .join(', ');

  const handleAdd = () => {
    onAdd(item)();
    scrollIntoViewIfNeeded();
  };

  return (
    <ItemCard
      dimmed={!item.available}
      image={
        <Badge badgeContent={quantity} color="primary">
          <DishImageTile urls={menuItemImages(item)} name={itemName} />
        </Badge>
      }
      title={title}
      left={
        <Typography component="p" variant="subtitle1">
          {formatCurrency(item.price)}
        </Typography>
      }
      rightRef={elementRef}
      right={
        editable ? (
          inCart ? (
            <Grid container spacing={1} wrap="nowrap">
              <Grid item>
                <Grow in>
                  <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAdd}
                    css={css`
                      padding-left: 6px;
                      padding-right: 6px;
                    `}
                  >
                    <AddIcon fontSize="small" />
                  </Button>
                </Grow>
              </Grid>

              <Grid item>
                <Grow in>
                  <Button
                    variant="contained"
                    color="error"
                    onClick={onRemove(item)}
                    css={css`
                      padding-left: 6px;
                      padding-right: 6px;
                    `}
                  >
                    <RemoveIcon fontSize="small" />
                  </Button>
                </Grow>
              </Grid>
            </Grid>
          ) : item.available ? (
            <Grow in>
              <Button
                variant="outlined"
                onClick={handleAdd}
                css={css`
                  min-width: 104px;
                `}
              >
                {t(TXT_SELECT)}
              </Button>
            </Grow>
          ) : (
            <MenuItemNotAvailable />
          )
        ) : null
      }
    />
  );
};

export default memo(MenuItemCard);
